/**
 * Selection Overlay
 * Renders selection boxes and resize handles for selected layers
 * Measures actual DOM elements to get accurate bounds
 */

import React, { useEffect, useState } from 'react';
import type { Layer } from '@/types/layers';
import type { AdFormat } from '@/types/formats';
import { type HandlePosition, getHandleCursor } from '@/lib/resize';

// Measured bounds for a layer in screen coordinates
interface LayerBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface SelectionOverlayProps {
  selectedLayerIds: string[];
  layers: Layer[];
  format: AdFormat;
  zoom: number;
  panOffset: { x: number; y: number };
  canvasOffset: { x: number; y: number };
  onResizeStart: (layerId: string, handle: HandlePosition, event: React.MouseEvent) => void;
  isResizing: boolean;
}

// Handle size in pixels (screen space, not scaled by zoom)
const HANDLE_SIZE = 8;

// Corner handles
const CORNER_HANDLES: HandlePosition[] = ['nw', 'ne', 'sw', 'se'];

export function SelectionOverlay({
  selectedLayerIds,
  layers,
  format,
  zoom,
  panOffset,
  canvasOffset,
  onResizeStart,
  isResizing,
}: SelectionOverlayProps) {
  // Store measured bounds for each selected layer
  const [layerBounds, setLayerBounds] = useState<Record<string, LayerBounds>>({});

  // Measure layer elements whenever selection, zoom, or pan changes
  useEffect(() => {
    if (selectedLayerIds.length === 0) {
      setLayerBounds({});
      return;
    }

    const measureLayers = () => {
      const canvas = document.getElementById('ad-canvas');
      if (!canvas) return;

      const canvasRect = canvas.getBoundingClientRect();
      const bounds: Record<string, LayerBounds> = {};

      for (const layerId of selectedLayerIds) {
        const element = canvas.querySelector(`[data-layer-id="${layerId}"]`);
        if (element) {
          const rect = element.getBoundingClientRect();
          // Convert to position relative to canvas, then to screen coordinates
          bounds[layerId] = {
            x: rect.left - canvasRect.left,
            y: rect.top - canvasRect.top,
            width: rect.width,
            height: rect.height,
          };
        }
      }

      setLayerBounds(bounds);
    };

    // Measure immediately and on next frame (for layout settling)
    measureLayers();
    const frameId = requestAnimationFrame(measureLayers);

    return () => cancelAnimationFrame(frameId);
  }, [selectedLayerIds, zoom, panOffset, format, layers]);

  if (selectedLayerIds.length === 0) return null;

  // Get handle position offsets for each corner
  const getHandleOffset = (handle: HandlePosition, width: number, height: number) => {
    const halfHandle = HANDLE_SIZE / 2;
    switch (handle) {
      case 'nw':
        return { x: -halfHandle, y: -halfHandle };
      case 'ne':
        return { x: width - halfHandle, y: -halfHandle };
      case 'sw':
        return { x: -halfHandle, y: height - halfHandle };
      case 'se':
        return { x: width - halfHandle, y: height - halfHandle };
      default:
        return { x: 0, y: 0 };
    }
  };

  return (
    <div
      className="absolute inset-0 pointer-events-none z-50"
      style={{ overflow: 'visible' }}
    >
      {selectedLayerIds.map((layerId) => {
        const layer = layers.find((l) => l.id === layerId);
        if (!layer) return null;
        if (layer.locked) return null; // Don't show handles for locked layers

        const bounds = layerBounds[layerId];
        if (!bounds) return null; // Not measured yet

        // Screen coordinates - canvasOffset gives us canvas position in container
        // bounds.x/y are relative to canvas (already scaled by CSS transform)
        const screenX = canvasOffset.x + bounds.x;
        const screenY = canvasOffset.y + bounds.y;
        const screenWidth = bounds.width;
        const screenHeight = bounds.height;

        return (
          <div key={layerId}>
            {/* Selection box */}
            <div
              style={{
                position: 'absolute',
                left: screenX,
                top: screenY,
                width: screenWidth,
                height: screenHeight,
                border: '2px solid #3B82F6',
                pointerEvents: 'none',
                boxSizing: 'border-box',
              }}
            />

            {/* Corner handles */}
            {!isResizing && CORNER_HANDLES.map((handle) => {
              const offset = getHandleOffset(handle, screenWidth, screenHeight);
              return (
                <div
                  key={`${layerId}-${handle}`}
                  style={{
                    position: 'absolute',
                    left: screenX + offset.x,
                    top: screenY + offset.y,
                    width: HANDLE_SIZE,
                    height: HANDLE_SIZE,
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #3B82F6',
                    cursor: getHandleCursor(handle),
                    pointerEvents: 'auto',
                    boxSizing: 'border-box',
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onResizeStart(layerId, handle, e);
                  }}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
